<template>
  <div class="page-card">
    <h2 class="page-title">轮播广告管理</h2>

    <div class="scene-grid">
      <div
        v-for="scene in scenes"
        :key="scene.sceneCode"
        class="scene-card"
        :class="{ active: query.sceneCode === scene.sceneCode }"
      >
        <div class="scene-card__top">
          <div class="scene-card__text" @click="selectScene(scene.sceneCode)">
            <div class="scene-card__title">{{ scene.sceneName }}</div>
            <div class="scene-card__meta">{{ scene.pageName }} / {{ scene.audienceName }}</div>
            <div class="scene-card__foot">最多 {{ scene.displayLimit || 6 }} 条轮播</div>
          </div>
          <div class="scene-card__actions">
            <el-tag :type="Number(scene.enabled) === 1 ? 'success' : 'info'">
              {{ Number(scene.enabled) === 1 ? '已显示' : '已隐藏' }}
            </el-tag>
            <el-button
              v-if="Number(scene.enabled) !== 1 && canSceneEnable"
              type="text"
              @click="changeSceneStatus(scene, 'enable')"
            >
              显示
            </el-button>
            <el-button
              v-if="Number(scene.enabled) === 1 && canSceneDisable"
              type="text"
              @click="changeSceneStatus(scene, 'disable')"
            >
              隐藏
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-select v-model="query.sceneCode" clearable placeholder="轮播区域" style="width: 220px">
          <el-option
            v-for="item in scenes"
            :key="item.sceneCode"
            :label="item.sceneName"
            :value="item.sceneCode"
          />
        </el-select>
        <el-select v-model="query.status" clearable placeholder="广告状态" style="width: 140px">
          <el-option label="待启用" :value="1" />
          <el-option label="展示中" :value="2" />
          <el-option label="已下线" :value="3" />
        </el-select>
        <el-input v-model="query.keyword" clearable placeholder="标题关键词" style="width: 220px" />
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
      <div class="toolbar-tip">
        建议素材尺寸：图片 `1500 × 600` 像素，视频 `1500 × 600` 或 `1080 × 432`，统一使用横版 `5:2` 比例可完美展示
      </div>
      <el-button v-if="canCreate" type="primary" @click="openDialog()">新增广告</el-button>
    </div>

    <el-table :data="list" border>
      <el-table-column prop="sceneName" label="轮播区域" min-width="140" />
      <el-table-column label="素材" width="120">
        <template slot-scope="{ row }">
          <div class="media-thumb">
            <img v-if="row.coverUrl" :src="resolveMediaUrl(row.coverUrl)" alt="cover">
            <span v-if="row.mediaType === 'video'" class="media-badge">视频</span>
            <span v-else class="media-badge media-badge-image">图片</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="180" />
      <el-table-column prop="subtitle" label="副标题" min-width="180" />
      <el-table-column label="跳转链接" min-width="180">
        <template slot-scope="{ row }">
          <span>{{ row.jumpUrl || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="sortNo" label="排序" width="90" />
      <el-table-column label="状态" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="statusTagType(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="220">
        <template slot-scope="{ row }">
          <el-button v-if="canUpdate" type="text" @click="openDialog(row)">编辑</el-button>
          <el-button v-if="Number(row.status) !== 2 && canEnable" type="text" @click="changeStatus(row, 'enable')">启用</el-button>
          <el-button v-if="Number(row.status) === 2 && canDisable" type="text" @click="changeStatus(row, 'disable')">下线</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="form.id ? '编辑轮播广告' : '新增轮播广告'" :visible.sync="dialogVisible" width="760px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="轮播区域" prop="sceneCode">
          <el-select v-model="form.sceneCode" placeholder="请选择轮播区域" style="width: 100%">
            <el-option
              v-for="item in scenes"
              :key="item.sceneCode"
              :label="item.sceneName"
              :value="item.sceneCode"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title" maxlength="60" show-word-limit />
        </el-form-item>
        <el-form-item label="副标题">
          <el-input v-model="form.subtitle" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="素材类型" prop="mediaType">
          <el-radio-group v-model="form.mediaType">
            <el-radio label="image">图片</el-radio>
            <el-radio label="video">视频</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="form.mediaType === 'video' ? '视频封面' : '图片地址'" prop="coverUrl">
          <div class="upload-row">
            <el-input v-model="form.coverUrl" placeholder="上传后自动回填，也可手动粘贴地址" />
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="(option) => uploadAsset(option, 'coverUrl')"
              :accept="imageAccept"
            >
              <el-button>上传图片</el-button>
            </el-upload>
          </div>
          <div v-if="form.coverUrl" class="preview-box">
            <img :src="resolveMediaUrl(form.coverUrl)" alt="preview">
          </div>
        </el-form-item>
        <el-form-item v-if="form.mediaType === 'video'" label="视频地址" prop="videoUrl">
          <div class="upload-row">
            <el-input v-model="form.videoUrl" placeholder="上传后自动回填，也可手动粘贴地址" />
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="(option) => uploadAsset(option, 'videoUrl')"
              :accept="videoAccept"
            >
              <el-button>上传视频</el-button>
            </el-upload>
          </div>
          <div v-if="form.videoUrl" class="video-preview-box">
            <video
              class="video-preview-player"
              :src="resolveMediaUrl(form.videoUrl)"
              :poster="resolveMediaUrl(form.videoPoster || form.coverUrl)"
              controls
              preload="metadata"
            >
              当前浏览器不支持视频预览
            </video>
          </div>
        </el-form-item>
        <el-form-item label="跳转链接">
          <el-input v-model="form.jumpUrl" placeholder="可选，不填则只展示不跳转" />
        </el-form-item>
        <el-form-item label="排序" prop="sortNo">
          <el-input-number v-model="form.sortNo" :min="0" :max="999" style="width: 100%" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">待启用</el-radio>
            <el-radio :label="2">立即展示</el-radio>
            <el-radio :label="3">先隐藏</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission } from '../../../utils/auth'
import {
  createBannerItem,
  disableBannerItem,
  enableBannerItem,
  getBannerItems,
  getBannerScenes,
  updateBannerItem
} from '../../../api/bannerManage'
import { resolveMediaUrl } from '../../../utils/format'
import request from '../../../utils/request'

const imageAccept = 'image/png,image/jpeg,image/jpg,image/webp'
const videoAccept = 'video/mp4,video/quicktime,video/x-m4v,video/3gpp,video/x-msvideo'

function getDefaultForm() {
  return {
    id: '',
    sceneCode: '',
    title: '',
    subtitle: '',
    mediaType: 'image',
    coverUrl: '',
    videoUrl: '',
    videoPoster: '',
    jumpUrl: '',
    sortNo: 0,
    status: 2
  }
}

export default {
  name: 'OpsBannerManagePage',
  data() {
    return {
      scenes: [],
      list: [],
      dialogVisible: false,
      query: {
        sceneCode: '',
        status: undefined,
        keyword: ''
      },
      form: getDefaultForm(),
      imageAccept,
      videoAccept,
      rules: {
        sceneCode: [{ required: true, message: '请选择轮播区域', trigger: 'change' }],
        mediaType: [{ required: true, message: '请选择素材类型', trigger: 'change' }],
        coverUrl: [{ required: true, message: '请上传图片或封面', trigger: 'blur' }],
        videoUrl: [{
          validator: (rule, value, callback) => {
            if (this.form.mediaType === 'video' && !value) {
              callback(new Error('请上传视频'))
              return
            }
            callback()
          },
          trigger: 'blur'
        }],
        sortNo: [{ required: true, message: '请输入排序', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  computed: {
    canCreate() {
      return hasPermission('ops_resource_item:create')
    },
    canUpdate() {
      return hasPermission('ops_resource_item:update')
    },
    canEnable() {
      return hasPermission('ops_resource_item:enable')
    },
    canDisable() {
      return hasPermission('ops_resource_item:disable')
    },
    canSceneEnable() {
      return hasPermission('ops_resource_position:enable')
    },
    canSceneDisable() {
      return hasPermission('ops_resource_position:disable')
    }
  },
  created() {
    this.loadInitialData()
  },
  methods: {
    resolveMediaUrl,
    statusLabel(value) {
      if (Number(value) === 2) return '展示中'
      if (Number(value) === 1) return '待启用'
      if (Number(value) === 3) return '已下线'
      return '-'
    },
    statusTagType(value) {
      if (Number(value) === 2) return 'success'
      if (Number(value) === 1) return 'warning'
      return 'info'
    },
    async loadInitialData() {
      this.scenes = await getBannerScenes()
      if (!this.query.sceneCode && this.scenes.length) {
        this.query.sceneCode = this.scenes[0].sceneCode
      }
      await this.fetchData()
    },
    async fetchData() {
      this.list = await getBannerItems(this.query)
    },
    selectScene(sceneCode) {
      this.query.sceneCode = sceneCode
      this.fetchData()
    },
    async changeSceneStatus(scene, action) {
      const sceneCode = scene && scene.sceneCode
      if (!sceneCode) return
      await request({
        url: `/api/admin/ops/banners/scenes/${sceneCode}/${action}`,
        method: 'post'
      })
      this.scenes = await getBannerScenes()
    },
    openDialog(row) {
      this.dialogVisible = true
      this.form = row
        ? {
            ...getDefaultForm(),
            ...row,
            videoPoster: row.videoPoster || row.coverUrl || ''
          }
        : {
            ...getDefaultForm(),
            sceneCode: this.query.sceneCode || (this.scenes[0] && this.scenes[0].sceneCode) || ''
          }
    },
    async uploadAsset(option, field) {
      const file = option && option.file
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const data = await request({
          url: '/api/common/upload',
          method: 'post',
          data: formData,
          timeout: 120000
        })
        if (!data || !data.url) {
          throw new Error('上传失败')
        }
        this.form[field] = data.url
        if (field === 'coverUrl' && this.form.mediaType === 'video') {
          this.form.videoPoster = data.url
        }
        this.$message.success('上传成功')
        if (option.onSuccess) option.onSuccess(data)
      } catch (error) {
        this.$message.error(error.message || '上传失败')
        if (option.onError) option.onError(error)
      }
    },
    submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        const payload = {
          ...this.form,
          videoPoster: this.form.mediaType === 'video' ? (this.form.videoPoster || this.form.coverUrl) : '',
          videoUrl: this.form.mediaType === 'video' ? this.form.videoUrl : ''
        }
        if (this.form.id) {
          await updateBannerItem(this.form.id, payload)
        } else {
          await createBannerItem(payload)
        }
        this.dialogVisible = false
        await this.fetchData()
        this.scenes = await getBannerScenes()
      })
    },
    async changeStatus(row, type) {
      if (type === 'enable') {
        await enableBannerItem(row.id)
      } else {
        await disableBannerItem(row.id)
      }
      await this.fetchData()
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.toolbar-tip {
  flex: 1;
  min-width: 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f4f8ff;
  border: 1px solid #d8e6ff;
  color: #5b6b88;
  font-size: 13px;
  line-height: 1.5;
}

.scene-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.scene-card {
  border: 1px solid #ebeef5;
  border-radius: 14px;
  padding: 16px;
  background: #fff;
  transition: all 0.2s ease;
}

.scene-card.active {
  border-color: #409eff;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.12);
}

.scene-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.scene-card__text {
  flex: 1;
  cursor: pointer;
}

.scene-card__actions {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 4px;
}

.scene-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}

.scene-card__meta {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.scene-card__foot {
  margin-top: 12px;
  color: #606266;
  font-size: 13px;
}

.media-thumb {
  position: relative;
  width: 84px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f7fa;
}

.media-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.78);
  color: #fff;
  font-size: 12px;
}

.media-badge-image {
  background: rgba(64, 158, 255, 0.85);
}

.upload-row {
  display: flex;
  gap: 12px;
}

.preview-box {
  margin-top: 12px;
  width: 180px;
  height: 108px;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  background: #f5f7fa;
}

.preview-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview-box {
  margin-top: 12px;
  width: 100%;
  max-width: 360px;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  background: #0f172a;
  overflow: hidden;
}

.video-preview-player {
  display: block;
  width: 100%;
  height: 204px;
  background: #000;
}
</style>
